import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {GeneralComponent} from '../general/general.component';
import {ChickInfoComponent} from '../chick-info/chick-info.component';
import {TripInfoComponent} from '../trip-info/trip-info.component';
import {MedicationRecordComponent} from '../medication-record/medication-record.component';
import {TabCommunicationService} from '@services/tab-communication.service';
import {MortalityComponent} from '../mortality/mortality.component';
import {MortalityStatsComponent} from '../mortality-stats/mortality-stats.component';
import {MedicationStatsComponent} from '../medication-stats/medication-stats.component';
import {VaccinationRecordComponent} from '../vaccination-record/vaccination-record.component';
import {VaccinationStatsComponent} from '../vaccination-stats/vaccination-stats.component';
import {BodyWeightRecordComponent} from '../body-weight-record/body-weight-record.component';
import {BodyWeightStatsComponent} from '../body-weight-stats/body-weight-stats.component';
import {PerformanceRecordComponent} from '../performance-record/performance-record.component';
import {FeedingRecordComponent} from '../feeding-record/feeding-record.component';
import {HasRoleDirective} from '@/has-role.directive';
import {
    TimelineRecordComponent,
    TimelineRecord
} from '../timeline-record/timeline-record.component';
import {
    PlacementService,
    GetCycleActivitiesResponse,
    Activity
} from '@services/placement.service';
import {
    DashboardKeyMetricsService,
    GetAIInsightsHouseResponse
} from '@services/dashboard-key-metrics.service';
import {AiInsightTileComponent} from '@components/medication/ai-insight-tile/ai-insight-tile.component';
import {ProgressBarModule} from 'primeng/progressbar';
import {HouseDetailsWidgetComponent} from '@components/dashboard/house-details-overview/house-details-widget/house-details-widget.component';

@Component({
    selector: 'app-insights-panel',
    standalone: true,
    imports: [
        CommonModule,
        GeneralComponent,
        ChickInfoComponent,
        TripInfoComponent,
        MedicationRecordComponent,
        MortalityComponent,
        MortalityStatsComponent,
        VaccinationRecordComponent,
        VaccinationStatsComponent,
        MedicationStatsComponent,
        BodyWeightRecordComponent,
        BodyWeightStatsComponent,
        PerformanceRecordComponent,
        FeedingRecordComponent,
        TimelineRecordComponent,
        AiInsightTileComponent,
        ProgressBarModule,
        HouseDetailsWidgetComponent,
        HasRoleDirective
    ],
    templateUrl: './insights-panel.component.html',
    styleUrl: './insights-panel.component.scss'
})
export class InsightsPanelComponent implements OnInit, OnChanges {
    activeTab: string = 'general';
    @Input() placement: any;
    @Input() placementId: number;
    @Input() medications: any;
    @Input() averageWeights: any;
    @Input() onOpenMedicationForm?: () => void;
    @Input() onOpenMortalityForm?: () => void;
    @Input() onOpenVaccinationForm?: () => void;
    @Input() onOpenBodyWeightForm?: () => void;
    @Output() tabChanged = new EventEmitter<string>();
    @Output() openMedicationForm = new EventEmitter<void>();
    @Output() openMortalityForm = new EventEmitter<void>();
    @Output() openVaccinationForm = new EventEmitter<void>();
    @Output() openBodyWeightForm = new EventEmitter<void>();

    private lastLoadedPlacementId: number | null = null;

    mortalitySummary: any = {
        totalMortalities: 0,
        mortalityRate: 0,
        todayMortalities: 0,
        weeklyMortalities: 0
    };

    timelineRecords: TimelineRecord[] = [];
    aiInsightsData: GetAIInsightsHouseResponse | null = null;
    loading = false;

    constructor(
        private tabCommunicationService: TabCommunicationService,
        private placementService: PlacementService,
        private dashboardService: DashboardKeyMetricsService
    ) {}

    ngOnInit(): void {
        this.tabCommunicationService.setActiveTab(this.activeTab);
        if (this.placementId) {
            this.loadAIInsights(this.placementId);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        const shouldReload =
            (changes['medications'] ||
                changes['mortalities'] ||
                changes['vaccinations'] ||
                changes['averageWeights']) &&
            this.placementId &&
            this.placementId !== this.lastLoadedPlacementId;

        if (shouldReload) {
            this.lastLoadedPlacementId = this.placementId;
            this.loadTimeline(this.placementId);
        }
    }

    private loadAIInsights(cycleId: number) {
        this.loading = true;
        this.dashboardService.getAIInsightsHouseResponse(cycleId).subscribe({
            next: (response) => {
                this.aiInsightsData = response;
                this.loading = false;
            },
            error: (error) => {
                console.error('Failed to load AI insights:', error);
                this.loading = false;
            }
        });
    }

    private loadTimeline(cycleId: number) {
        this.placementService.getCycleActivities(cycleId).subscribe({
            next: (res: GetCycleActivitiesResponse) => {
                this.timelineRecords = this.mapActivitiesToTimelineRecords(res);
            },
            error: () => {
                this.timelineRecords = [];
            }
        });
    }

    private mapActivitiesToTimelineRecords(
        res: GetCycleActivitiesResponse
    ): TimelineRecord[] {
        const toRecords = (
            arr: Activity[],
            type: 'medication' | 'vaccination' | 'disinfection'
        ): TimelineRecord[] => {
            return (arr || []).map((a) => {
                const status = this.deriveStatus(a.date, a.time);
                const datePart = this.formatDate(a.date);
                const timePart = a.time || '';
                return {
                    type,
                    status,
                    impact: 'medium',
                    date: datePart,
                    time: timePart,
                    title: this.capitalize(type),
                    product: {
                        name: a.product,
                        quantity: String(a.quantity)
                    },
                    performedBy: undefined,
                    notes: undefined
                } as TimelineRecord;
            });
        };

        return [
            ...toRecords(res.medication || [], 'medication'),
            ...toRecords(res.vaccination || [], 'vaccination'),
            ...toRecords(res.disinfection || [], 'disinfection')
        ].sort(
            (r1, r2) =>
                new Date(`${r2.date}T${r2.time || '00:00'}`).getTime() -
                new Date(`${r1.date}T${r1.time || '00:00'}`).getTime()
        );
    }

    private deriveStatus(
        dateIso: string,
        time: string | undefined
    ): 'done' | 'in-progress' | 'not-started' {
        const d = this.combineDateTime(dateIso, time);
        const now = new Date();
        return d.getTime() <= now.getTime() ? 'done' : 'not-started';
    }

    private combineDateTime(dateIso: string, time: string | undefined): Date {
        try {
            const d = new Date(dateIso);
            if (time) {
                const [hh, mm] = time.split(':').map((v) => parseInt(v, 10));
                d.setHours(hh || 0, mm || 0, 0, 0);
            }
            return d;
        } catch {
            return new Date(dateIso);
        }
    }

    private formatDate(dateIso: string): string {
        const d = new Date(dateIso);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const da = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${da}`;
    }

    private capitalize(s: string): string {
        return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
    }

    @Input() mortalities: any;
    @Input() vaccinations: any;
    @Input() bodyWeights: any;
    @Input() timeline: any;
    @Input() performance: any;
    @Input() feeding: any;

    tabs = [
        {id: 'general', label: 'General'},
        {id: 'mortality', label: 'Mortality'},
        {id: 'medication', label: 'Medication'},
        {id: 'vaccination', label: 'Vaccination'},
        {id: 'feeding', label: 'Feeding'},
        {id: 'body-weight', label: 'Body Weight'},
        {id: 'timeline', label: 'Timeline'},
        {id: 'performance', label: 'Performance'},
        {id: 'ai-insights', label: 'AI Insights'}
    ];

    setActiveTab(tabId: string): void {
        this.activeTab = tabId;
        this.tabChanged.emit(tabId);
        this.tabCommunicationService.setActiveTab(tabId);
    }

    isActiveTab(tabId: string): boolean {
        return this.activeTab === tabId;
    }

    openMedicationDialog(): void {
        if (this.onOpenMedicationForm) {
            this.onOpenMedicationForm();
        } else {
            this.openMedicationForm.emit();
        }
    }

    openMortalityDialog(): void {
        if (this.onOpenMortalityForm) {
            this.onOpenMortalityForm();
        } else {
            this.openMortalityForm.emit();
        }
    }

    openVaccinationDialog(): void {
        if (this.onOpenVaccinationForm) {
            this.onOpenVaccinationForm();
        } else {
            this.openVaccinationForm.emit();
        }
    }

    openBodyWeightDialog(): void {
        if (this.onOpenBodyWeightForm) {
            this.onOpenBodyWeightForm();
        } else {
            this.openBodyWeightForm.emit();
        }
    }

    getInsightColor(severity: 'low' | 'medium' | 'high' | 'info'): string {
        switch (severity) {
            case 'info':
                return 'blue';
            case 'low':
                return 'green';
            case 'medium':
                return 'orange';
            case 'high':
                return 'red';
            default:
                return 'blue';
        }
    }
}
